import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { normalizeCompanyIdentifier, setStoredCompanyIdentifier, clearAuthStorage } from "../utils/companyIdentifier";
import {
  setCompanyDetails as setCompanyDetailsRedux,
  setThemeColor as setThemeColorRedux,
  setCompanyLogoUrl,
  setCompanyName as setCompanyNameRedux,
  setCompanyLoading,
  setCompanyError
} from "../redux/slices/companySlice.js";

// Use environment variable for API base URL
const BACKEND_API = process.env.REACT_APP_BACKEND_API;

const TenantContext = createContext(null);

// GLOBAL module-level cache - survives React remounts and StrictMode
let globalFetchedForTenant = null;
let globalIsFetching = false;
let globalCachedDetails = null;

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within TenantProvider");
  }
  return context;
};

// Backend validates tenant via token + companyIdentifier header
// 401/403 responses trigger logout via axios interceptor

export const TenantProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [tenantName, setTenantName] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Company branding state
  const [companyDetails, setCompanyDetails] = useState(globalCachedDetails);
  const [themeColor, setThemeColor] = useState(globalCachedDetails?.themeColor || "#2563eb");
  const [companyLogo, setCompanyLogo] = useState(() => {
    if (globalCachedDetails?.logo) {
      // Check if it's a base64 data URI or full URL
      if (globalCachedDetails.logo.startsWith('data:') || globalCachedDetails.logo.startsWith('http')) {
        return globalCachedDetails.logo;
      }
      // Otherwise, filenames will be fetched later via fetchCompanyLogo after login
      return null;
    }
    return null;
  });
  const [companyName, setCompanyName] = useState(() => {
    if (globalCachedDetails?.companyIdentifier) {
      return globalCachedDetails.companyIdentifier.charAt(0).toUpperCase() + globalCachedDetails.companyIdentifier.slice(1);
    }
    return "FlxFlow";
  });

  // Load tenant from localStorage on mount
  useEffect(() => {
    const storedTenantName = normalizeCompanyIdentifier(localStorage.getItem("companyIdentifier"));
    const storedTenantId = sessionStorage.getItem("tenantId");

    if (storedTenantName && storedTenantId) {
      setStoredCompanyIdentifier(storedTenantName);
      setTenantName(storedTenantName);
      setTenantId(storedTenantId);
    }
  }, []);

  // Helper function to dispatch company data to Redux
  const dispatchToRedux = (result) => {
    dispatch(setCompanyDetailsRedux(result));

    if (result.themeColor) {
      dispatch(setThemeColorRedux(result.themeColor));
    }

    if (result.logo) {
      const logoUrl = (result.logo.startsWith('data:') || result.logo.startsWith('http'))
        ? result.logo
        : null; // Filenames are fetched via fetchCompanyLogo in userAction
      dispatch(setCompanyLogoUrl(logoUrl));
    }
    if (result.companyName) {
      dispatch(setCompanyNameRedux(result.companyName));
    } else if (result.companyIdentifier) {
      const formattedName = result.companyIdentifier.charAt(0).toUpperCase() + result.companyIdentifier.slice(1);
      dispatch(setCompanyNameRedux(formattedName));
    }
  };

  // Fetch company details for a tenant
  // Returns { success: boolean, result?, message? } so callers can validate the company
  const fetchCompanyDetails = async (identifier) => {
    const normalizedIdentifier = normalizeCompanyIdentifier(identifier);
    if (!normalizedIdentifier) return { success: false, message: "Company identifier is required" };

    // Use window object - survives ALL React behavior
    window.__companyDetailsCache = window.__companyDetailsCache || {};

    // Check if already fetched for this identifier
    if (window.__companyDetailsCache[normalizedIdentifier]) {
      const result = window.__companyDetailsCache[normalizedIdentifier];
      setCompanyDetails(result);
      if (result.themeColor) setThemeColor(result.themeColor);
      if (result.logo) {
        // Check if it's a base64 data URI or full URL
        const logoUrl = (result.logo.startsWith('data:') || result.logo.startsWith('http'))
          ? result.logo
          : null;
        setCompanyLogo(logoUrl);
      }
      if (result.companyIdentifier) {
        const formattedName = result.companyIdentifier.charAt(0).toUpperCase() + result.companyIdentifier.slice(1);
        setCompanyName(formattedName);
      }
      // Also sync to Redux
      dispatchToRedux(result);
      return { success: true, result };
    }

    // Check if currently fetching (using window flag)
    if (window.__isFetchingCompanyDetails) {
      return { success: false, message: "Company lookup already in progress" };
    }

    window.__isFetchingCompanyDetails = true;
    dispatch(setCompanyLoading(true));

    try {
      const response = await axios.get(
        `${BACKEND_API}/fetch-company-details?identifier=${normalizedIdentifier}`
      );

      if (response.data?.status === 200 && response.data?.result) {
        const result = response.data.result;

        // Cache in window object
        window.__companyDetailsCache[normalizedIdentifier] = result;

        // Also update global variables
        globalFetchedForTenant = normalizedIdentifier;
        globalCachedDetails = result;

        setCompanyDetails(result);

        if (result.themeColor) {
          setThemeColor(result.themeColor);
        }

        if (result.logo) {
          // Check if it's a base64 data URI or full URL
          const logoUrl = (result.logo.startsWith('data:') || result.logo.startsWith('http'))
            ? result.logo
            : null;
          setCompanyLogo(logoUrl);
        }

        if (result.companyName) {
          setCompanyName(result.companyName);
        } else if (result.companyIdentifier) {
          const formattedName = result.companyIdentifier.charAt(0).toUpperCase() + result.companyIdentifier.slice(1);
          setCompanyName(formattedName);
        }

        // Dispatch to Redux store
        dispatchToRedux(result);

        return { success: true, result };
      } else {
        dispatch(setCompanyError("Invalid company identifier"));
        return { success: false, message: "Invalid company identifier" };
      }
    } catch (error) {
      const isNotFound = error.response?.status === 404;
      const message = isNotFound
        ? "Invalid company identifier"
        : (error.response?.data?.message || "Unable to verify company. Please try again.");
      dispatch(setCompanyError(message));
      return { success: false, message };
    } finally {
      window.__isFetchingCompanyDetails = false;
      dispatch(setCompanyLoading(false));
    }
  };

  const setTenant = async (name) => {
    const normalizedName = normalizeCompanyIdentifier(name);
    if (!normalizedName) return { success: false, message: "Tenant name required" };

    setIsLoading(true);
    try {
      // Set tenant directly - backend validates via token + companyIdentifier header
      setTenantName(normalizedName);
      setStoredCompanyIdentifier(normalizedName);

      // Fetch company details for this tenant
      await fetchCompanyDetails(normalizedName);

      return { success: true, data: { tenantName: normalizedName } };
    } catch (error) {
      return { success: false, message: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const clearTenant = ({ preserveIdentifier = true } = {}) => {
    setTenantName(null);
    setTenantId(null);
    setCompanyDetails(null);
    setThemeColor("#2563eb");
    setCompanyLogo(null);
    setCompanyName("FlxFlow");
    // Reset global cache
    globalFetchedForTenant = null;
    globalCachedDetails = null;
    clearAuthStorage({ preserveIdentifier });
  };

  const value = {
    tenantName,
    tenantId,
    isLoading,
    hasTenant: !!tenantName,
    setTenant,
    clearTenant,
    // Company branding
    companyDetails,
    themeColor,
    companyLogo,
    companyName,
    fetchCompanyDetails,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
